import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, createClient, updateClient, deleteClient, exportClientsPdf } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, Search, Pencil, Trash2, Users, Loader2, MapPin, Mail, Phone, Eye, Receipt, Upload, FileSpreadsheet, Download } from 'lucide-react';
import { toast } from 'sonner';

const ClientsPage = () => {
  const { t, language } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '', email: '', address: '', city: '', postal_code: '', country: 'Suisse', phone: '', vat_number: '', internal_notes: '', default_currency: 'CHF', default_language: 'fr'
  });
  const navigate = useNavigate();

  const getLocale = () => {
    switch(language) {
      case 'de': return 'de-CH';
      case 'en': return 'en-GB';
      default: return 'fr-CH';
    }
  };

  const formatCurrencyLocal = (amount) => new Intl.NumberFormat(getLocale(), { style: 'currency', currency: 'CHF' }).format(amount);

  useEffect(() => { loadClients(); }, []);

  const loadClients = async () => {
    try {
      const response = await getClients();
      setClients(response.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', address: '', city: '', postal_code: '', country: 'Suisse', phone: '', vat_number: '', default_currency: 'CHF', default_language: 'fr' });
    setEditingClient(null);
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name, email: client.email || '', address: client.address, city: client.city,
      postal_code: client.postal_code, country: client.country, phone: client.phone || '', vat_number: client.vat_number || '', internal_notes: client.internal_notes || '', default_currency: client.default_currency || 'CHF', default_language: client.default_language || 'fr'
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.address || !formData.city || !formData.postal_code) {
      toast.error('Champs obligatoires manquants');
      return;
    }
    setSaving(true);
    try {
      if (editingClient) {
        await updateClient(editingClient.id, formData);
        toast.success('Client mis à jour');
      } else {
        await createClient(formData);
        toast.success('Client créé');
      }
      setDialogOpen(false);
      resetForm();
      loadClients();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!clientToDelete) return;
    try {
      await deleteClient(clientToDelete);
      toast.success('Client supprimé');
      loadClients();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/clients/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { imported, skipped, errors } = response.data;
      
      if (imported > 0) {
        toast.success(`${imported} client(s) importé(s)`);
      }
      if (skipped > 0) {
        toast.info(`${skipped} client(s) ignoré(s) (doublons ou invalides)`);
      }
      if (errors.length > 0) {
        toast.warning(`${errors.length} erreur(s)`);
      }
      
      loadClients();
      setImportDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csv = "Nom;Email;Téléphone;Adresse;Ville;Code postal;Pays;N° TVA\nExemple SA;contact@exemple.ch;+41 21 123 45 67;Rue de la Gare 1;Lausanne;1003;Suisse;CHE-123.456.789";
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modele_import_clients.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredClients = clients.filter((c) =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">{t('clients.title')}</h1>
          <p className="text-slate-500 mt-1">{language === 'fr' ? 'Gérez votre base de clients' : language === 'de' ? 'Verwalten Sie Ihre Kundenbasis' : 'Manage your client base'}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="import-csv-btn">
            <Upload className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">{t('clients.importCsv')}</span>
          </Button>
          <Button variant="outline" onClick={async () => {
            try {
              const res = await exportClientsPdf();
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'clients.pdf');
              document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
            } catch { toast.error('Erreur export'); }
          }} data-testid="export-clients-pdf">
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">PDF</span>
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="new-client-btn">
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">{t('clients.create')}</span>
            <span className="sm:hidden">{t('common.add')}</span>
          </Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder={t('common.search') + '...'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
          </div>
        </CardContent>
      </Card>

      {filteredClients.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <Card key={client.id} className="border-slate-200 card-hover" data-testid={`client-card-${client.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center cursor-pointer" onClick={() => navigate(`/clients/${client.id}`)}>
                    <span className="text-blue-700 font-bold text-lg">{client.name.charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/clients/${client.id}`)} title={t('common.view')}><Eye className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(client)} data-testid={`edit-client-${client.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => { setClientToDelete(client.id); setDeleteDialogOpen(true); }} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                
                <h3 className="font-semibold text-slate-900 mb-1 cursor-pointer hover:text-blue-600" onClick={() => navigate(`/clients/${client.id}`)}>
                  {client.name}
                  {client.default_currency && client.default_currency !== 'CHF' && (
                    <span className="ml-2 text-xs font-medium px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">{client.default_currency}</span>
                  )}
                </h3>
                {client.vat_number && <p className="text-xs text-slate-500 mb-2">{t('common.tax')}: {client.vat_number}</p>}
                
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>{client.postal_code} {client.city}</span>
                  </div>
                  {client.email && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span className="truncate">{client.email}</span>
                    </div>
                  )}
                  {client.phone && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{client.phone}</span>
                    </div>
                  )}
                </div>

                <div className="border-t pt-3 flex justify-between text-sm">
                  <div>
                    <p className="text-slate-500">{t('clients.totalBilled')}</p>
                    <p className="font-medium">{formatCurrencyLocal(client.total_invoiced || 0)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500">{language === 'fr' ? 'En attente' : language === 'de' ? 'Ausstehend' : 'Pending'}</p>
                    <p className="font-medium text-amber-600">{formatCurrencyLocal(client.total_pending || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="empty-state py-12">
            <Users className="empty-state-icon" />
            <h3 className="empty-state-title">{t('clients.noClients')}</h3>
            <p className="empty-state-description">{t('clients.createFirst')}</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />{t('common.add')}
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingClient ? t('common.edit') : t('clients.create')}</DialogTitle>
            <DialogDescription>{t('clients.clientDetails')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('clients.name')} *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="client-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('clients.email')}</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="client-email-input" />
              </div>
              <div className="space-y-2">
                <Label>{t('clients.phone')}</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="client-phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('settings.vatNumber')} ({language === 'fr' ? 'optionnel' : language === 'de' ? 'optional' : 'optional'})</Label>
              <Input value={formData.vat_number} onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })} placeholder="CHE-XXX.XXX.XXX" />
            </div>
            <div className="space-y-2">
              <Label>{t('clients.address')} *</Label>
              <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} required data-testid="client-address-input" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('clients.postalCode')} *</Label>
                <Input value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} required data-testid="client-postal-input" />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>{t('clients.city')} *</Label>
                <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required data-testid="client-city-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('clients.country')}</Label>
                <Input value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Devise</Label>
                <select
                  value={formData.default_currency}
                  onChange={(e) => setFormData({ ...formData, default_currency: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  data-testid="client-currency-select"
                >
                  <option value="CHF">CHF - Franc suisse</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="USD">USD - Dollar US</option>
                  <option value="GBP">GBP - Livre sterling</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Langue des documents</Label>
              <select
                value={formData.default_language}
                onChange={(e) => setFormData({ ...formData, default_language: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="client-language-select"
              >
                <option value="fr">Francais</option>
                <option value="de">Deutsch</option>
                <option value="en">English</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Notes internes <span className="text-xs text-slate-400">(visible uniquement par votre equipe)</span></Label>
              <textarea
                value={formData.internal_notes}
                onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
                placeholder="Ex: Client VIP, paie toujours a temps..."
                rows={2}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="client-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="save-client-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingClient ? t('common.save') : t('common.create'))}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.delete')} ?</AlertDialogTitle>
            <AlertDialogDescription>{language === 'fr' ? 'Cette action est irréversible.' : language === 'de' ? 'Diese Aktion kann nicht rückgängig gemacht werden.' : 'This action cannot be undone.'}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import CSV Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              {t('clients.importCsv')}
            </DialogTitle>
            <DialogDescription>
              Importez vos clients depuis un fichier CSV
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4 text-sm">
              <p className="font-medium text-slate-900 mb-2">Format attendu :</p>
              <p className="text-slate-600">Colonnes : Nom, Email, Téléphone, Adresse, Ville, Code postal, Pays, N° TVA</p>
              <p className="text-slate-500 mt-2">Séparateur : point-virgule (;) ou virgule (,)</p>
            </div>
            
            <Button 
              variant="outline" 
              onClick={downloadTemplate}
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger le modèle CSV
            </Button>
            
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm font-medium text-slate-700">
                  {importing ? 'Import en cours...' : 'Cliquez pour sélectionner un fichier CSV'}
                </p>
                <p className="text-xs text-slate-500 mt-1">ou glissez-déposez</p>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
